// ============================================================================
// PAGE - nodeRedDashboard.jsx
// ============================================================================
// client/src/pages/labEngine/nodeRedDashboard.jsx
//
// Follows the same load pattern as arduinoSimulator and notebookSimulator:
//   1. Load course info
//   2. Load simulator by sessionId  (deviceSimulator collection)
//   3. Load circuit  by sessionId   (visualCircuit collection)
//   4. Enrich components with componentLibrary data
//   5. Derive flow from circuit.components[].nodeRedFlow  (design source of truth)
//      Supplement with simulator.attachedNodeRedGateways[0] (runtime status)

import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import showNotification from '../../utilities/showNotification';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import CustomButton from '../../components/customs/customButton';
import BackButton from '../../components/customs/backButton';
import ProfIXChatbot from '../../components/chatBot/ProfIXChatbot';
import NodeRedUISection from '../../components/labEngine/NodeRedUISection';
import SimulationReport from '../../modals/labEngine/simulationReport';
import SensorSimulation from '../../modals/labEngine/sensorSimulation';
import { GlobalContext } from '../../context/provider';
import CourseDomains from '../../types/courseDomains';


import { useCourse, useVisualCircuit, 
  useSimulator, useComponentLibrary
} from '../../hooks';


import '../../styles/globalstyles.css';
import './nodeRedDashboard.css';

// ─── Constants ───────────────────────────────────────────────────────────────
const NODE_COLORS = {
  nr_input:      '#3FADB5',
  nr_processing: '#FAB040',
  nr_output:     '#87A980',
};

const NODE_ICONS = {
  nr_input:      '📥',
  nr_processing: '⚙️',
  nr_output:     '📤',
};

const CATEGORY_LABELS = {
  nr_input:      'Input Nodes',
  nr_processing: 'Processing Nodes',
  nr_output:     'Output Nodes',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatPill = ({ label, value, color }) => (
  <div className="nrd-stat-pill">
    <span className="nrd-stat-value" style={{ color: color || '#1e293b' }}>{value}</span>
    <span className="nrd-stat-label">{label}</span>
  </div>
);

// Flow overview diagram — left-to-right column view
const FlowOverview = ({ nodes = [], wires = [], nodeLastData = {} }) => {
  const enrichedNodes = nodes.map(n => ({
    ...n,
    liveRaw:   nodeLastData[n.nodeRedType]?.raw   ?? null,
    liveHuman: nodeLastData[n.nodeRedType]?.human ?? null,
  }));
  
  const byCategory = {
    nr_input:      enrichedNodes.filter(n => n.category === 'nr_input'),
    nr_processing: enrichedNodes.filter(n => n.category === 'nr_processing'),
    nr_output:     enrichedNodes.filter(n => n.category === 'nr_output'),
  };

  if (nodes.length === 0) {
    return (
      <div className="nrd-flow-empty">
        <p>No nodes in this flow. Open the flow canvas to add nodes.</p>
      </div>
    );
  }

  return (
    <div className="nrd-flow-overview">
      {['nr_input', 'nr_processing', 'nr_output'].map((cat, colIdx) => (
        <React.Fragment key={cat}>
          <div className="nrd-flow-col">
            <div className="nrd-flow-col-header" style={{ color: NODE_COLORS[cat] }}>
              {NODE_ICONS[cat]} {CATEGORY_LABELS[cat]}
              <span className="nrd-flow-col-count">{byCategory[cat].length}</span>
            </div>
            {byCategory[cat].length === 0 ? (
              <div className="nrd-flow-col-empty">—</div>
            ) : (
              byCategory[cat].map(node => (
              <div
                key={node.instanceId || node.nodeId}
                className={`nrd-flow-node-pill ${!node.enabled ? 'nrd-flow-node-pill--disabled' : ''}`}
                style={{ borderColor: NODE_COLORS[cat] }}
              >
                <span className="nrd-flow-node-label">{node.label || node.componentName}</span>
                <span className="nrd-flow-node-type">{node.nodeRedType}</span>
                {!node.enabled && <span className="nrd-disabled-tag">off</span>}
                {/* Live data rows — passed via prop */}
                {node.liveRaw && (
                  <span className="nrd-flow-node-live-raw">{node.liveRaw}</span>
                )}
                {node.liveHuman && (
                  <span className="nrd-flow-node-live-human">{node.liveHuman}</span>
                )}
              </div>
              ))
            )}
          </div>
          {/* Arrow between columns */}
          {colIdx < 2 && (
            <div className="nrd-flow-arrow">
              <span>→</span>
              <span className="nrd-wire-count">{wires.length > 0 && colIdx === 0 ? `${wires.length}` : ''}</span>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Single node detail card — expandable
const NodeCard = ({ node, runtimeNode }) => {
  const [expanded, setExpanded] = useState(false);
  const color = NODE_COLORS[node.category] || '#aaa';

  // Merge design config with runtime status
  const status = runtimeNode?.status || {};

  const inputPins  = (node.pinDetails || []).filter(p => p.direction === 'input'  && p.enabled);
  const outputPins = (node.pinDetails || []).filter(p => p.direction === 'output' && p.enabled);

  return (
    <div className="nrd-node-card" style={{ borderLeft: `4px solid ${color}` }}>
      {/* Card header — always visible */}
      <div className="nrd-node-card-header" onClick={() => setExpanded(e => !e)}>
        <span className="nrd-node-type-tag" style={{ background: color }}>
          {node.nodeRedType}
        </span>
        <span className="nrd-node-card-name">{node.label || node.componentName}</span>
        <span className={`nrd-node-enabled-dot ${node.enabled ? 'on' : 'off'}`}>
          {node.enabled ? '● Active' : '○ Disabled'}
        </span>
        {/* Runtime message count badge */}
        {status.messagesIn !== undefined && (
          <span className="nrd-msg-badge">
            ↓{status.messagesIn ?? 0}
          </span>
        )}
        {status.messagesOut !== undefined && (
          <span className="nrd-msg-badge nrd-msg-badge--out">
            ↑{status.messagesOut ?? 0}
          </span>
        )}
        <span className="nrd-node-card-chevron">{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div className="nrd-node-card-body">

          {/* ── Communication configuration ───────────────────────────── */}
          {node.communicationConfigs && Object.keys(node.communicationConfigs).length > 0 && (
            <div className="nrd-card-section">
              <h5>⚙️ Configuration</h5>
              {Object.entries(node.communicationConfigs).map(([protocol, cfg]) => (
                <div key={protocol} className="nrd-config-block">
                  <div className="nrd-config-block-header">
                    <strong>{protocol.toUpperCase()}</strong>
                    <span className={`nrd-enabled-tag ${cfg.enabled ? 'on' : 'off'}`}>
                      {cfg.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                  {cfg.enabled && cfg.settings && (
                    <div className="nrd-config-settings-grid">
                      {Object.entries(cfg.settings)
                        .filter(([k]) =>
                          !k.toLowerCase().includes('password') &&
                          !k.toLowerCase().includes('key') &&
                          !k.toLowerCase().includes('secret')
                        )
                        .map(([k, v]) => (
                          <div key={k} className="nrd-setting-row">
                            <span className="nrd-setting-key">
                              {k.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                            <span className="nrd-setting-val">
                              {typeof v === 'boolean' ? (v ? '✓' : '✗') : String(v) || '—'}
                            </span>
                          </div>
                        ))}
                      {/* Masked fields */}
                      {Object.keys(cfg.settings).some(k =>
                        k.toLowerCase().includes('password') ||
                        k.toLowerCase().includes('key') ||
                        k.toLowerCase().includes('secret')
                      ) && (
                        <div className="nrd-setting-row">
                          <span className="nrd-setting-key">credentials</span>
                          <span className="nrd-setting-val nrd-masked">••••••••</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Active ports ─────────────────────────────────────────── */}
          {(inputPins.length > 0 || outputPins.length > 0) && (
            <div className="nrd-card-section">
              <h5>🔌 Ports</h5>
              <div className="nrd-ports-grid">
                {inputPins.map(pin => (
                  <div key={pin.pinName} className="nrd-port-row nrd-port-row--input">
                    <span className="nrd-port-dir" style={{ color: NODE_COLORS.nr_input }}>←</span>
                    <span className="nrd-port-name">{pin.pinName}</span>
                    {pin.settings && Object.keys(pin.settings).length > 0 && (
                      <div className="nrd-port-settings">
                        {Object.entries(pin.settings)
                          .filter(([, v]) => v !== '' && v !== null && v !== undefined)
                          .slice(0, 3)
                          .map(([k, v]) => (
                            <span key={k} className="nrd-port-setting-chip">
                              {k}: <strong>{String(v)}</strong>
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
                {outputPins.map(pin => (
                  <div key={pin.pinName} className="nrd-port-row nrd-port-row--output">
                    <span className="nrd-port-dir" style={{ color: NODE_COLORS.nr_output }}>→</span>
                    <span className="nrd-port-name">{pin.pinName}</span>
                    {pin.settings && Object.keys(pin.settings).length > 0 && (
                      <div className="nrd-port-settings">
                        {Object.entries(pin.settings)
                          .filter(([, v]) => v !== '' && v !== null && v !== undefined)
                          .slice(0, 3)
                          .map(([k, v]) => (
                            <span key={k} className="nrd-port-setting-chip">
                              {k}: <strong>{String(v)}</strong>
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Runtime status (from simulator) ──────────────────────── */}
          {Object.keys(status).length > 0 && (
            <div className="nrd-card-section">
              <h5>📊 Runtime</h5>
              <div className="nrd-runtime-grid">
                {status.messagesIn !== undefined && (
                  <div className="nrd-runtime-stat">
                    <span>Messages In</span>
                    <strong>{status.messagesIn ?? 0}</strong>
                  </div>
                )}
                {status.messagesOut !== undefined && (
                  <div className="nrd-runtime-stat">
                    <span>Messages Out</span>
                    <strong>{status.messagesOut ?? 0}</strong>
                  </div>
                )}
                {status.lastActivity && (
                  <div className="nrd-runtime-stat">
                    <span>Last Activity</span>
                    <strong>{new Date(status.lastActivity).toLocaleTimeString()}</strong>
                  </div>
                )}
                {status.lastMessage && (
                  <div className="nrd-runtime-stat nrd-runtime-stat--wide">
                    <span>Last Message</span>
                    <strong className="nrd-mono">
                      {typeof status.lastMessage === 'object'
                        ? JSON.stringify(status.lastMessage).slice(0, 120)
                        : String(status.lastMessage)}
                    </strong>
                  </div>
                )}
                {status.lastError && (
                  <div className="nrd-runtime-stat nrd-runtime-stat--wide nrd-runtime-stat--error">
                    <span>Last Error</span>
                    <strong>{status.lastError}</strong>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
};

const LiveDataPanel = ({ liveData }) => {
  const noData = <span className="nrd-live-no-data">No data yet</span>;

  const sections = [
    {
      icon: '🏭',
      title: 'Sensors → PLC',
      subtitle: 'Raw sensor readings entering the PLC',
      color: '#6366f1',
      content: liveData ? (
        <div className="nrd-live-sensor-grid">
          {Object.entries(liveData.sensorText).map(([key, val]) => (
            <div key={key} className="nrd-live-sensor-chip">
              <span className="nrd-live-sensor-key">{key}</span>
              <span className="nrd-live-sensor-val">{String(val)}</span>
            </div>
          ))}
        </div>
      ) : noData,
    },
    {
      icon: '📡',
      title: `PLC → Gateway`,
      subtitle: liveData ? `Protocol: ${liveData.plcToGateway.protocol}` : 'Protocol: —',
      color: '#3FADB5',
      content: liveData ? (
        <pre className="nrd-live-protocol-block">
          {liveData.plcToGateway.formatted}
        </pre>
      ) : noData,
    },
    {
      icon: '🔴',
      title: 'Gateway → Azure',
      subtitle: liveData ? `Protocol: ${liveData.gatewayToAzure.protocol}` : 'Protocol: —',
      color: '#FAB040',
      content: liveData ? (
        <pre className="nrd-live-protocol-block">
          {liveData.gatewayToAzure.formatted}
        </pre>
      ) : noData,
    },
    {
      icon: '☁️',
      title: 'Azure → Decoded',
      subtitle: 'Message received and decoded by Azure IoT Hub',
      color: '#0078d4',
      content: liveData ? (
        <div className="nrd-live-sensor-grid">
          {Object.entries(liveData.azureDecoded).map(([key, val]) => (
            <div key={key} className="nrd-live-sensor-chip">
              <span className="nrd-live-sensor-key">{key}</span>
              <span className="nrd-live-sensor-val">{String(val)}</span>
            </div>
          ))}
        </div>
      ) : noData,
    },
  ];

  return (
    <div className="nrd-live-panel">
      {sections.map((section, i) => (
        <React.Fragment key={section.title}>
          <div className="nrd-live-section" style={{ borderLeft: `4px solid ${section.color}` }}>
            <div className="nrd-live-section-header">
              <span className="nrd-live-section-icon">{section.icon}</span>
              <div>
                <div className="nrd-live-section-title">{section.title}</div>
                <div className="nrd-live-section-subtitle">{section.subtitle}</div>
              </div>
              {liveData && (
                <span className="nrd-live-timestamp">
                  {new Date(liveData.timestamp).toLocaleTimeString()}
                </span>
              )}
            </div>
            <div className="nrd-live-section-body">
              {section.content}
            </div>
          </div>
          {/* Arrow between sections */}
          {i < sections.length - 1 && (
            <div className="nrd-live-arrow">↓</div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
const NodeRedDashboard = () => {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const navigate  = useNavigate();

  // ── State ──────────────────────────────────────────────────────────────────
  const [circuit,    setCircuit]    = useState(null);
  const [simulator,  setSimulator]  = useState(null);
  const [courseInfo, setCourseInfo] = useState(null);
  const [isLoading,  setIsLoading]  = useState(true);
  const [showSimulationReport, setShowSimulationReport] = useState(false);
  const [activeTab,  setActiveTab]  = useState('all'); // 'all' | 'nr_input' | 'nr_processing' | 'nr_output'
  const [sensorModalVisible, setSensorModalVisible] = useState(false);
  const [selectedSensor, setSelectedSensor]         = useState(null);
  const [componentStates, setComponentStates] = useState({ sensors: [] });
  const [iterationCount, setIterationCount] = useState(0);
  // Add this near other state declarations:
  const [stableEngineRunning, setStableEngineRunning] = useState(false);

  const cap = CourseDomains.IIoT.polling.cap; // 20

  // ── Hooks ──────────────────────────────────────────────────────────────────
  const {
    getSimulatorBySessionId,
    saveGradingPayload, 
    submitGrades, 
    updateSensorConfig,
    currentSimulator,
    snapshotCount,
    // engineRunning   : isRunning,
    engineRunning   : pollActive,
    liveData,
    messagesRouted,
    mqttConnected,
    resetSimulator,
    // engineLoading: pollActive,
    engineLoading,
    getEngineStatus,
    handleEngineRun,
    stopEngine,
    generateNodeRedFunction : generateFunction,
    isGenerating,
    generateError,
    setGenerateError,
    engineSensors,
  } = useSimulator();

  const { getCircuitBySessionId }                       = useVisualCircuit();
  const { getComponentById }                            = useComponentLibrary();
  const { getCourseById }                               = useCourse();
  const { authState: { authData } }                     = useContext(GlobalContext);

  const stableCircuit = useMemo(() => circuit, [circuit?._id]);
  // Derive these once from stableCircuit — they never change after load
  const nrGatewayComp = useMemo(() => 
    stableCircuit?.components?.find(c => c.isFlowEngine), 
    [stableCircuit?._id]
  );
  
  // ── Derived data ───────────────────────────────────────────────────────────
  // Source of truth for the flow design → circuit's gateway component nodeRedFlow
  const gatewayCircuitComp = circuit?.components?.find(c => c.isFlowEngine);
  const flowNodes  = gatewayCircuitComp?.nodeRedFlow?.nodes  || [];
  const flowWires  = gatewayCircuitComp?.nodeRedFlow?.wires   || [];
  const flowLabel  = gatewayCircuitComp?.nodeRedFlow?.flowLabel || circuit?.name || '';

  // Runtime status supplement → simulator's attachedNodeRedGateways
  const simGateway  = simulator?.attachedNodeRedGateways?.[0];
  const simNodes    = simGateway?.flowNodes || [];


  // Merge: for each design node, find matching runtime node by instanceId
  const getSimNode = (instanceId) =>
    simNodes.find(n => n.nodeId === instanceId);

  // ── Load data (mirrors arduinoSimulator/notebookSimulator pattern) ─────────
  const loadData = useCallback(async () => {
    try {
      if (!authData?.id) return;
      if (!sessionId) {
        alert('⚠️ No sessionId in URL.');
        return;
      }

      // 1. Course info
      if (courseId) {
        const course = await getCourseById(courseId);
        if (course.success) setCourseInfo(course.data);
      }

      // 2. Simulator by sessionId
      const simResponse = await getSimulatorBySessionId(sessionId);
      if (simResponse.success) {
        setSimulator(simResponse.data);
        console.log('✅ Simulator loaded:', simResponse.data.simulatorId);
      } else {
        console.warn('⚠️ Simulator not found for sessionId:', sessionId);
        // Don't block — circuit is the primary source for flow design
      }

      // 3. Circuit by sessionId
      const circuitResponse = await getCircuitBySessionId(sessionId);
      if (!circuitResponse.success) {
        alert('⚠️ Circuit not found. Please sync your circuit first.');
        navigate(-1);
        return;
      }

      // 4. Enrich components with componentLibrary data (same as arduinoSimulator)
      const enrichedCircuit = { ...circuitResponse.data };
      enrichedCircuit.components = await Promise.all(
        circuitResponse.data.components.map(async (comp) => {
          if (comp.componentId) {
            try {
              const libResponse = await getComponentById(comp.componentId);
              if (libResponse?.success) {
                return { ...comp, componentLibraryData: libResponse.data };
              }
            } catch (err) {
              console.error('Error fetching component library data:', err);
            }
          }
          return comp;
        })
      );

      setCircuit(enrichedCircuit);
      console.log('✅ Circuit loaded:', enrichedCircuit.circuitId);

    } catch (err) {
      console.error('❌ Error loading Node-RED dashboard:', err);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, courseId, authData?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Initialize sensor list from circuit on load — same as arduinoSimulator
  useEffect(() => {
    if (circuit && circuit.components) {
      const sensorsFromCircuit = circuit.components
        .filter(c => c.category === 'sensors')
        .map(circuitComp => ({
          sensorId:    circuitComp.instanceId,
          name:        circuitComp.label || circuitComp.componentName,
          type:        circuitComp.componentId,
          componentId: circuitComp.componentId,
          connections: circuitComp.connections || [],
          currentValue: null,
          simulationConfig: simulator?.attachedSensors?.find(
            s => s.sensorId === circuitComp.instanceId
          )?.simulationConfig || null,
        }));
      setComponentStates({ sensors: sensorsFromCircuit });
    }
  }, [circuit, simulator?.attachedSensors]);

  useEffect(() => {
    if (!pollActive) setIterationCount(0);
  }, [pollActive]);

  // Add this effect to debounce pollActive changes:
  useEffect(() => {
    const timer = setTimeout(() => {
      setStableEngineRunning(!!pollActive);
    }, 500); // 500ms debounce
    return () => clearTimeout(timer);
  }, [pollActive]);

  useEffect(() => {
    console.log('[Dashboard] pollActive changed:', pollActive);
  }, [pollActive]);

  // Build sensor list from circuit components (same as arduinoSimulator)
  // Merge with live currentValue from engineSensors if engine running
  const sensors = (() => {
    const fromCircuit = (circuit?.components || [])
      .filter(c => c.category === 'sensors')
      .map(c => ({
        sensorId:     c.instanceId,
        name:         c.label || c.componentName,
        type:         c.componentId,
        componentId:  c.componentId,
        connections:  c.connections || [],
        currentValue: simulator?.attachedSensors?.find(
          s => s.sensorId === c.instanceId
        )?.currentValue ?? null,
        simulationConfig: simulator?.attachedSensors?.find(
          s => s.sensorId === c.instanceId
        )?.simulationConfig ?? null,
      }));

    // Overlay live currentValue from engine if running
    if (engineSensors?.length > 0) {
      return fromCircuit.map(s => {
        const live = engineSensors.find(e => e.sensorId === s.sensorId);
        return live ? { ...s, currentValue: live.currentValue } : s;
      });
    }
    return fromCircuit;
  })();

  const actuators = (() => {
    return (circuit?.components || [])
      .filter(c => c.category === 'actuators')
      .map(c => {
        // Find AO connection from circuit.connections
        const conn = circuit?.connections?.find(conn =>
          (conn.from.componentInstanceId === c.instanceId && conn.to.pinName?.startsWith('AO')) ||
          (conn.to.componentInstanceId === c.instanceId && conn.from.pinName?.startsWith('AO'))
        );
        const aoPin = conn
          ? (conn.from.componentInstanceId === c.instanceId ? conn.to.pinName : conn.from.pinName)
          : null;

        const attachedActuator = simulator?.attachedActuators?.find(
          a => a.actuatorId === c.instanceId
        );

        return {
          actuatorId:   c.instanceId,
          name:         c.label || c.componentName,
          type:         c.componentId,
          aoPin,
          currentValue: attachedActuator?.currentValue ?? null,
          currentState: attachedActuator?.currentState ?? null,
        };
      });
  })();

  // ── Grading handler (mirrors notebookSimulator pattern) ───────────────────
  const handlePostToIIoTSession = async (freshSim = null) => {
    const sim = freshSim || simulator;  // ✅ use fresh if provided
    if (!sim) return;

    const isGradedLab = sessionId && sessionId.includes('_LAB');
    if (!isGradedLab) {
      alert('ℹ️ This is a practice flow. No grading available.');
      return;
    }
    if (!simulator) return;
    // if (simulator?.gradingPayload) {
    //   showNotification('Data has already been submitted and cannot be overridden', 'warning');
    //   return;
    // }

    // ── Sanitise settings ────────────────────────────────────────────────────
    const sanitiseSettings = (settings = {}) => {
      const SENSITIVE = ['password', 'key', 'secret', 'token', 'credential'];
      return Object.fromEntries(
        Object.entries(settings).filter(
          ([k]) => !SENSITIVE.some(s => k.toLowerCase().includes(s))
        )
      );
    };

    // ── Extract flow nodes with safe config summaries ─────────────────────────
    const safeFlowNodes = flowNodes.map(node => ({
      instanceId:    node.instanceId,
      componentName: node.componentName,
      nodeRedType:   node.nodeRedType,
      category:      node.category,
      label:         node.label || node.componentName,
      enabled:       node.enabled ?? true,
      configSummary: Object.fromEntries(
        Object.entries(node.communicationConfigs || {}).map(([protocol, cfg]) => [
          protocol,
          { enabled: cfg.enabled, settings: sanitiseSettings(cfg.settings || {}) }
        ])
      ),
      activePins: (node.pinDetails || [])
        .filter(p => p.enabled)
        .map(p => ({
          pinName:         p.pinName,
          direction:       p.direction,
          softwarePinType: p.softwarePinType,
          settingsSummary: sanitiseSettings(p.settings || {})
        })),
    }));

    // ── Extract wires ──────────────────────────────────────────────────────────
    const safeFlowWires = flowWires.map(w => {
      const fromNode = flowNodes.find(n => n.instanceId === w.from?.nodeInstanceId);
      const toNode   = flowNodes.find(n => n.instanceId === w.to?.nodeInstanceId);
      return {
        wireId:   w.wireId,
        fromNode: fromNode?.label || fromNode?.componentName || w.from?.nodeInstanceId,
        fromPort: w.from?.portName,
        toNode:   toNode?.label   || toNode?.componentName   || w.to?.nodeInstanceId,
        toPort:   w.to?.portName,
      };
    });

    // ── Gateway config ─────────────────────────────────────────────────────────
    const gatewayConfig = Object.fromEntries(
      Object.entries(gatewayCircuitComp?.communicationConfigs || {}).map(
        ([protocol, cfg]) => [
          protocol,
          { enabled: cfg.enabled, settings: sanitiseSettings(cfg.settings || {}) }
        ]
      )
    );

    // ── Flow stats ─────────────────────────────────────────────────────────────
    const inputNodes      = flowNodes.filter(n => n.category === 'nr_input'      && n.enabled);
    const processingNodes = flowNodes.filter(n => n.category === 'nr_processing' && n.enabled);
    const outputNodes     = flowNodes.filter(n => n.category === 'nr_output'     && n.enabled);

    const hasCompletePath = inputNodes.length > 0 && outputNodes.length > 0 &&
      flowWires.some(w => {
        const fromNode = flowNodes.find(n => n.instanceId === w.from?.nodeInstanceId);
        const toNode   = flowNodes.find(n => n.instanceId === w.to?.nodeInstanceId);
        return fromNode?.category === 'nr_input' &&
          toNode && (toNode.category === 'nr_processing' || toNode.category === 'nr_output');
      });

    const flowStats = {
      totalNodes:       flowNodes.length,
      enabledNodes:     flowNodes.filter(n => n.enabled).length,
      inputNodes:       inputNodes.length,
      processingNodes:  processingNodes.length,
      outputNodes:      outputNodes.length,
      totalWires:       flowWires.length,
      hasInputs:        inputNodes.length > 0,
      hasProcessing:    processingNodes.length > 0,
      hasOutputs:       outputNodes.length > 0,
      hasCompletePath,
      enabledProtocols: Object.entries(gatewayCircuitComp?.communicationConfigs || {})
        .filter(([, cfg]) => cfg.enabled)
        .map(([p]) => p),
    };

    // ── Collect IIoT evidence — matches CourseDomains.IIoT.gradingPayloadFields ──
    const gradingData = {
      flowNodes:           safeFlowNodes,
      flowWires:           safeFlowWires,
      flowStats,
      gatewayConfig,
      simulationSnapshots: sim?.simulationSnapshots || [],   // ✅ mirrors IoT
    };
    

    try {
      const duration = simulator?.state?.uptime || 0;
      const response = await saveGradingPayload(sim._id, duration, gradingData);

      if (response.success) {
        setSimulator(prev => ({ ...prev, gradingPayload: response.data }));
        showNotification('Flow submitted for grading!', 'success');
      } else {
        showNotification(`Failed to submit: ${response.error}`, 'error');
      }
    } catch (err) {
      showNotification('Failed to submit flow for grading', 'error');
    }
  };

  const handleGrade = async () => {
    if (pollActive) await stopEngine(sessionId);

    // ✅ Fetch fresh simulator first
    let freshSimulator = simulator;
    const res = await getSimulatorBySessionId(sessionId);
    if (res.success) {
      freshSimulator = res.data;
      setSimulator(res.data);
    }

    // ✅ Post with fresh data — pass freshSimulator explicitly
    await handlePostToIIoTSession(freshSimulator);

    setShowSimulationReport(true);
  };

  const handleRun = async () => {
    if (pollActive) {
      // If already running, just stop
      await stopEngine(sessionId);
      return;
    }
    // Reset simulator snapshots before new run
    if (simulator?._id) {
      await resetSimulator(simulator._id);
    }
    // Start engine
    await handleEngineRun(sessionId);
  };

  const handleSubmitGrades = async () => {
    if (!simulator?._id) return;
    const response = await submitGrades(simulator._id);
    if (response.success) {
      setSimulator(prev => ({ ...prev, gradingPayload: response.data }));
      showNotification('Grades submitted!', 'success');
    } else {
      showNotification(`Failed to submit grades: ${response.error}`, 'error');
    }
  };

  const handleSaveSensorConfig = useCallback(async (sensorId, config) => {
    if (!simulator?._id) return;
    try {
      // Same pattern as arduinoSimulator — update simulationConfig in DB
      // const response = await axiosInstance.patch(
      //   `/api/simulator/${simulator._id}/sensor/${sensorId}/config`,
      //   { simulationConfig: config }
      // );
      const response = await updateSensorConfig(simulator._id, sensorId, config)
      if (response.data?.success) {
        showNotification('Sensor simulation config saved', 'success');
      }
    } catch (err) {
      showNotification('Failed to save sensor config', 'error');
    }
  }, [simulator?._id]);

  // ── Filter nodes by tab ───────────────────────────────────────────────────
  const filteredNodes = activeTab === 'all'
    ? flowNodes
    : flowNodes.filter(n => n.category === activeTab);

  // ── Header data ───────────────────────────────────────────────────────────
  const profileHeaderData = {
    ...authData,
    title: '🔴 Node-RED Dashboard',
    // subTitle: circuit?.name || 'Node-RED Flow',
  };

  // ── Status ────────────────────────────────────────────────────────────────
  const statusKey = pollActive
    ? 'running'
    : simGateway?.status?.deployed
      ? 'deployed'
      : simGateway?.status?.lastError
        ? 'error'
        : 'stopped';

  const STATUS_BADGE = {
    running:  { label: '● Running',  color: '#10b981' },
    stopped:  { label: '○ Stopped',  color: '#6b7280' },
    error:    { label: '✕ Error',    color: '#ef4444' },
    deployed: { label: '✓ Deployed', color: '#3b82f6' },
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="main-container">
      <div className="main-content">

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="header-row">
          <ProfileHeader data={profileHeaderData} activePage="simulator" />
          <div className="headerMiddle">
            {/* ── ProfIX Chatbot ───────────────────────────────────────────────── */}
            <ProfIXChatbot
              userName={authData?.name}
              courseId={courseId}
              courseName={courseInfo?.title}
              category={courseInfo?.category}
            />
          </div>
          <SharedHeader notificationCount={0} icons={[]} />
        </div>

        {/* ── Toolbar ─────────────────────────────────────────────────────── */}
        <div className="ai-notebook-toolbar">
          <div className="ai-notebook-subtoolbar">
            <div className="ai-notebook-toolbar-left">
              <BackButton 
                  to="-1"
                  activeTab="my-courses"
                  label="←"
                  bgColor="#6b7280"
              />
              {sessionId && sessionId.includes('_LAB') && (
                <>
                  {/* Show Report — only when gradingPayload exists */}
                  {simulator?.gradingPayload && (
                    <CustomButton
                      buttonTitle="📊 Show Report"
                      action={() => setShowSimulationReport(true)}
                      bgColor="#8b5cf6"
                      disabled={!simulator}
                    />
                  )}

                  {/* Grade — always shown, disabled only when graded */}
                  <CustomButton
                    buttonTitle="📤 Grade"
                    action={handleGrade}
                    bgColor="#8b5cf6"
                    disabled={
                      !simulator ||
                      simulator?.gradingPayload?.status === 'graded'
                    }
                  />
                </>
              )}
              {/* {circuit && ( */}
              <span className="ai-notebook-flow-name">
                {/* {circuit && circuit.labId && (
                  <span className="ai-notebook-lab-tag">📚 {circuit.labId}</span>
                )} */}
                {circuit && circuit.name && (<span>🔴 {circuit.name}</span>)}
                <span className="ai-notebook-session-id">Session: {sessionId}</span>
              </span>
              {/* )} */}
            </div>

          </div>
        </div>
        
        {/* <div className="nrd-toolbar-right"> */}
        <div className="ai-notebook-toolbar-right" style={{marginLeft: '20px'}}>
          <span
            className="nrd-status-badge"
            style={{ background: STATUS_BADGE[statusKey].color }}
          >
            {STATUS_BADGE[statusKey].label}
          </span>
          
          {/* Live updates */}
          <button
            className={`nrd-poll-btn ${pollActive ? 'active' : ''}`}
            onClick={handleRun}
            disabled={isLoading || engineLoading}
          >
            {isLoading ? '⏳' : pollActive ? '⏸' : '▶'}
          </button>

          {/* ✅ Countdown — only shows while polling */}
          {pollActive && (
            <span className="nrd-poll-countdown" title="Snapshots captured">
              {/* 🔄 {iterationCount}/{cap} */}
              🔄 {snapshotCount}/{CourseDomains.IIoT.polling.cap}
            </span>
          )}

          <button className="nrd-refresh-btn" onClick={loadData}>
            🔄
          </button>
        </div>

        {/* ── Loading ──────────────────────────────────────────────────────── */}
        {isLoading && (
          <div className="loading-overlay">
            <div className="loader" />
            <p>Loading Node-RED dashboard…</p>
          </div>
        )}

        {/* ── Main content ────────────────────────────────────────────────── */}
        <div className="nrd-content">
          {!isLoading && (
            <>
            {/* ── Top stats row ──────────────────────────────────────────── */}
            <div className="nrd-stats-row">
              <div className="nrd-stats-card">
                <div className="nrd-stats-card-value" style={{ color: NODE_COLORS.nr_input }}>
                  {flowNodes.filter(n => n.category === 'nr_input').length}
                </div>
                <div className="nrd-stats-card-label">📥 Input Nodes</div>
              </div>
              <div className="nrd-stats-card">
                <div className="nrd-stats-card-value" style={{ color: NODE_COLORS.nr_processing }}>
                  {flowNodes.filter(n => n.category === 'nr_processing').length}
                </div>
                <div className="nrd-stats-card-label">⚙️ Processing Nodes</div>
              </div>
              <div className="nrd-stats-card">
                <div className="nrd-stats-card-value" style={{ color: NODE_COLORS.nr_output }}>
                  {flowNodes.filter(n => n.category === 'nr_output').length}
                </div>
                <div className="nrd-stats-card-label">📤 Output Nodes</div>
              </div>
              <div className="nrd-stats-card">
                <div className="nrd-stats-card-value" style={{ color: '#94a3b8' }}>
                  {flowWires.length}
                </div>
                <div className="nrd-stats-card-label">🔗 Connections</div>
              </div>
              <div className="nrd-stats-card">
                <div className="nrd-stats-card-value" style={{ color: '#10b981' }}>
                  {flowNodes.filter(n => n.enabled).length}
                </div>
                <div className="nrd-stats-card-label">✓ Active</div>
              </div>
            </div>

            {/* ── Gateway config strip ────────────────────────────────────── */}
            {gatewayCircuitComp && (
              <section className="nrd-section">
                <h3 className="nrd-section-title">🔴 Gateway — {gatewayCircuitComp.label}</h3>

                {/* Enabled protocols only */}
                <div className="nrd-gateway-protocols">
                  {Object.entries(gatewayCircuitComp.communicationConfigs || {})
                    .filter(([protocol, cfg]) => cfg.enabled)
                    .map(([protocol, cfg]) => (
                      <div
                        key={protocol}
                        className="nrd-protocol-card nrd-protocol-card--on"
                      >
                        <div className="nrd-protocol-card-header">
                          <strong>{protocol.toUpperCase()}</strong>
                          <span className="nrd-enabled-tag on">Enabled</span>
                        </div>
                        {cfg.settings && (
                          <div className="nrd-protocol-settings">
                            {Object.entries(cfg.settings)
                              .filter(([k, v]) =>
                                v !== '' && v !== null && v !== undefined &&
                                !k.toLowerCase().includes('password') &&
                                !k.toLowerCase().includes('key')
                              )
                              .slice(0, 4)
                              .map(([k, v]) => (
                                <div key={k} className="nrd-setting-row">
                                  <span className="nrd-setting-key">
                                    {k.replace(/([A-Z])/g, ' $1').trim()}
                                  </span>
                                  <span className="nrd-setting-val">
                                    {typeof v === 'boolean' ? (v ? '✓' : '✗') : String(v)}
                                  </span>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    ))}
                </div>

                {/* Active ports */}
                {(gatewayCircuitComp.pinDetails || []).filter(p => p.enabled).length > 0 && (
                  <div className="nrd-gateway-ports">
                    {gatewayCircuitComp.pinDetails.filter(p => p.enabled).map(pin => (
                      <div key={pin.pinName} className="nrd-gateway-port-chip">
                        <span className={`nrd-port-dir-icon ${pin.direction}`}>
                          {pin.direction === 'input' ? '←' : '→'}
                        </span>
                        <strong>{pin.pinName}</strong>
                        {pin.settings?.topic && (
                          <span className="nrd-port-topic">{pin.settings.topic}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* ── Flow overview diagram ────────────────────────────────────── */}
            <section className="nrd-section">
              <h3 className="nrd-section-title">
                🗺️ Flow Overview
                {flowLabel && <span className="nrd-flow-label-tag">{flowLabel}</span>}
              </h3>
              <FlowOverview
                nodes={flowNodes}
                wires={flowWires}
                nodeLastData={liveData?.nodeLastData ?? {}}
              />
            </section>

            {/* ── Connected Sensors ──────────────────────────────────────────────────── */}
            {sensors.length > 0 && (
              <section className="nrd-section">
                <h3 className="nrd-section-title">🌡️ Connected Sensors</h3>
                <div className="nrd-sensor-grid">
                  {sensors.map(sensor => {
                    const val = sensor.currentValue;
                    const displayVal = val
                      ? typeof val === 'object'
                        ? Object.entries(val)
                            .map(([k, v]) => `${k}: ${typeof v === 'number' ? v.toFixed(2) : v}`)
                            .join(' | ')
                        : String(val)
                      : 'No data';

                    // Find which AI pin it's connected to
                    const aiPin = (() => {
                      const conn = circuit?.connections?.find(c =>
                        (c.from.componentInstanceId === sensor.sensorId && c.to.pinName?.startsWith('AI')) ||
                        (c.to.componentInstanceId === sensor.sensorId && c.from.pinName?.startsWith('AI'))
                      );
                      if (!conn) return null;
                      const aiPinName = conn.to.componentInstanceId === sensor.sensorId
                        ? conn.to.pinName
                        : conn.from.pinName;
                      // Actually the AI pin is on the PLC side, not the sensor side
                      const plcPinName = conn.from.componentInstanceId === sensor.sensorId
                        ? conn.to.pinName
                        : conn.from.pinName;
                      return plcPinName?.startsWith('AI') ? { boardPin: plcPinName } : null;
                    })();

                    // In the sensors.map inside the section:
                    const attachedSensor = simulator?.attachedSensors?.find(
                      a => a.sensorId === sensor.sensorId
                    );
                    const sensorConfig = attachedSensor?.simulationConfig;

                    return (
                      <div key={sensor.sensorId} className="nrd-sensor-card">
                        <div className="nrd-sensor-card-header">
                          <span className="nrd-sensor-name">{sensor.name}</span>
                          {aiPin && (
                            <span className="nrd-sensor-pin-badge">{aiPin.boardPin}</span>
                          )}
                          <button
                            className="nrd-sensor-config-btn"
                            onClick={() => {
                              const attachedSensor = simulator?.attachedSensors?.find(
                                a => a.sensorId === sensor.sensorId
                              );
                              setSelectedSensor({
                                ...sensor,
                                id: sensor.sensorId,
                                simulationConfig: attachedSensor?.simulationConfig || null,
                              });
                              setSensorModalVisible(true);
                            }}
                            title="Configure simulation"
                          >
                            ⚙️
                          </button>
                        </div>
                        <div className="nrd-sensor-value">{displayVal}</div>
                        <div className="nrd-sensor-type">{sensor.type}</div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ── Connected Actuators ──────────────────────────────────────────────────── */}
            {actuators.length > 0 && (
              <section className="nrd-section">
                <h3 className="nrd-section-title">⚙️ Actuators (AO Channels)</h3>
                <div className="nrd-sensor-grid">
                  {actuators.map(actuator => (
                    <div key={actuator.actuatorId} className="nrd-sensor-card">
                      <div className="nrd-sensor-card-header">
                        <span className="nrd-sensor-name">{actuator.name}</span>
                        {actuator.aoPin && (
                          <span className="nrd-sensor-pin-badge">{actuator.aoPin}</span>
                        )}
                      </div>
                      <div className="nrd-sensor-value">
                        {actuator.currentValue !== null
                          ? String(actuator.currentValue)
                          : '— no data'}
                      </div>
                      <div className="nrd-sensor-type">{actuator.type}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* ── SensorSimulation modal ──────────────────────────────────────────────── */}
            <SensorSimulation
              visible={sensorModalVisible}
              sensor={selectedSensor}
              onClose={() => setSensorModalVisible(false)}
              onSave={handleSaveSensorConfig}
            />

            {/* ── Live Data ────────────────────────────────────────────────────────── */}
            <section className="nrd-section">
              <div className="nrd-section-title-row">
                <h3 className="nrd-section-title">
                  📡 Live Data
                  {liveData && (
                    <span className="nrd-flow-label-tag">
                      Updated: {new Date(liveData.timestamp).toLocaleTimeString()}
                    </span>
                  )}
                </h3>
                {!pollActive && !liveData && (
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                    Start the engine ▶ to see live data
                  </span>
                )}
              </div>
              <LiveDataPanel liveData={liveData} />
            </section>

            {/* ── Node cards with tab filter ───────────────────────────────── */}
            <section className="nrd-section">
              <div className="nrd-section-title-row">
                <h3 className="nrd-section-title">🔍 Node Details</h3>
                <div className="nrd-tabs">
                  {['all', 'nr_input', 'nr_processing', 'nr_output'].map(tab => (
                    <button
                      key={tab}
                      className={`nrd-tab ${activeTab === tab ? 'active' : ''}`}
                      style={activeTab === tab && tab !== 'all'
                        ? { borderColor: NODE_COLORS[tab], color: NODE_COLORS[tab] }
                        : {}}
                      onClick={() => setActiveTab(tab)}
                    >
                      {tab === 'all' ? 'All' : `${NODE_ICONS[tab]} ${CATEGORY_LABELS[tab]}`}
                      <span className="nrd-tab-count">
                        {tab === 'all'
                          ? flowNodes.length
                          : flowNodes.filter(n => n.category === tab).length}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {filteredNodes.length === 0 ? (
                <div className="nrd-nodes-empty">
                  <p>
                    {flowNodes.length === 0
                      ? 'No nodes in this flow yet. Open the flow canvas to add nodes.'
                      : `No ${CATEGORY_LABELS[activeTab]?.toLowerCase() || 'nodes'} in this flow.`}
                  </p>
                </div>
              ) : (
                <div className="nrd-node-list">
                  {filteredNodes.map(node => (
                    <NodeCard
                      key={node.instanceId}
                      node={node}
                      runtimeNode={getSimNode(node.instanceId)}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* ── flows.json preview (collapsible) ────────────────────────── */}
            {gatewayCircuitComp?.nodeRedFlow?.flowsJson?.length > 0 && (
              <section className="nrd-section">
                <details className="nrd-flows-json-details">
                  <summary className="nrd-section-title" style={{ cursor: 'pointer' }}>
                    📄 Generated flows.json ({gatewayCircuitComp.nodeRedFlow.flowsJson.length} entries)
                  </summary>
                  <pre className="nrd-flows-json">
                    {JSON.stringify(gatewayCircuitComp.nodeRedFlow.flowsJson, null, 2)}
                  </pre>
                </details>
              </section>
            )}

            {/* ── Empty state ──────────────────────────────────────────────── */}
            {!gatewayCircuitComp && (
              <div className="nrd-empty-state">
                <div className="nrd-empty-icon">🔴</div>
                <h3>No Node-RED Gateway found</h3>
                <p>
                  Add a Node-RED Gateway to the bridge canvas, build your flow,
                  then click <strong>Save & Validate</strong> and <strong>Sync</strong>.
                </p>
                <button className="nrd-back-btn" onClick={() => navigate(-1)}>
                  ← Back to Canvas
                </button>
              </div>
            )}
            </>
          )}

          {/* ── Dashboard UI Widgets ─────────────────────────────────────── */}
          {/* Pass primitives only — no object references */}
          <NodeRedUISection
            circuitId={stableCircuit?._id}
            gatewayComp={nrGatewayComp}
            isEngineRunning={pollActive}
            liveData={liveData}
          />

        </div>

        {/* ── Simulation Report modal ──────────────────────────────────────── */}
        <SimulationReport
          visible={showSimulationReport}
          simulator={simulator}
          circuit={circuit}
          domain="IIoT"
          onClose={() => setShowSimulationReport(false)}
          onSubmit={handleSubmitGrades}
          // ✅ Pass liveData so dashboard tab renders correctly.
          // Falls back to last saved snapshot if engine already stopped.
          liveData={
            liveData ||
            (simulator?.simulationSnapshots?.length > 0
              ? simulator.simulationSnapshots[simulator.simulationSnapshots.length - 1]
              : null)
          }
          onRefreshSimulator={async () => {
            const res = await getSimulatorBySessionId(sessionId);
            if (res.success) setSimulator(res.data);
          }}
        />

      </div>
    </div>
  );
};

export default NodeRedDashboard;
